import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/auth/AuthContext";
import { ClerkWithRouter } from "@/auth/ClerkWithRouter";
import { LocaleProvider } from "@/i18n/LocaleContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { PlatformLayout } from "@/components/layout/PlatformLayout";
import { EmployeeLayout } from "@/components/layout/EmployeeLayout";
import Login from "./pages/Login";
import InviteAccept from "./pages/InviteAccept";
import Dashboard from "./pages/Dashboard";
import Attendance from "./pages/Attendance";
import Employees from "./pages/Employees";
import Departments from "./pages/Departments";
import Requests from "./pages/Requests";
import Sites from "./pages/Sites";
import Holidays from "./pages/Holidays";
import Shifts from "./pages/Shifts";
import Roster from "./pages/Roster";
import SiteForm from "./pages/SiteForm";
import Reports from "./pages/Reports";
import Assistant from "./pages/Assistant";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";

import MyDay from "./pages/me/MyDay";
import MyAttendance from "./pages/me/MyAttendance";
import MySchedule from "./pages/me/MySchedule";
import MyRequests from "./pages/me/MyRequests";
import MyProfile from "./pages/me/MyProfile";
import MyNotifications from "./pages/me/MyNotifications";
import MyTeam from "./pages/me/MyTeam";

import PlatformDashboard from "./pages/platform/PlatformDashboard";
import PlatformTenants from "./pages/platform/PlatformTenants";
import TenantDetail from "./pages/platform/TenantDetail";
import TenantUsers from "./pages/platform/TenantUsers";
import Users from "./pages/Users";
import Subscriptions from "./pages/platform/Subscriptions";
import SitesOverview from "./pages/platform/SitesOverview";
import AIControl from "./pages/platform/AIControl";
import GlobalNotifications from "./pages/platform/GlobalNotifications";
import AuditLogs from "./pages/platform/AuditLogs";
import PlatformSettings from "./pages/platform/PlatformSettings";
import PlatformProfile from "./pages/platform/PlatformProfile";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Clerk must sit inside the router so its prebuilt widgets navigate
            between flow steps client-side instead of reloading the page. */}
        <ClerkWithRouter>
          <LocaleProvider>
            <AuthProvider>
              <Routes>
              {/*
                Clerk's <SignIn /> uses path-based routing; the splat route lets
                it own /login/factor-one, /login/forgot-password, etc.
              */}
              <Route path="/login/*" element={<Login />} />
              <Route path="/invite-accept/*" element={<InviteAccept />} />

              {/* Tenant Admin Portal */}
              <Route element={<AppLayout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/employees" element={<Employees />} />
                <Route path="/departments" element={<Departments />} />
                <Route path="/users" element={<Users />} />
                <Route path="/requests" element={<Requests />} />
                <Route path="/sites" element={<Sites />} />
                <Route path="/sites/new" element={<SiteForm />} />
                <Route path="/sites/:id/edit" element={<SiteForm />} />
                <Route path="/holidays" element={<Holidays />} />
                <Route path="/shifts" element={<Shifts />} />
                <Route path="/roster" element={<Roster />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/assistant" element={<Assistant />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
              </Route>

              {/* Employee lane (FEAT-2026-013). Gated on the Employee
                  principal, which most employees have *instead of* a User
                  row — so this must not sit inside AppLayout. */}
              <Route element={<EmployeeLayout />}>
                <Route path="/me" element={<MyDay />} />
                <Route path="/me/attendance" element={<MyAttendance />} />
                <Route path="/me/schedule" element={<MySchedule />} />
                <Route path="/me/requests" element={<MyRequests />} />
                <Route path="/me/profile" element={<MyProfile />} />
                <Route path="/me/notifications" element={<MyNotifications />} />
                <Route path="/me/team" element={<MyTeam />} />
              </Route>

              {/* Super Admin Platform Console */}
              <Route element={<PlatformLayout />}>
                <Route path="/platform" element={<PlatformDashboard />} />
                <Route path="/platform/tenants" element={<PlatformTenants />} />
                <Route path="/platform/tenants/users" element={<TenantUsers />} />
                <Route path="/platform/tenants/:id" element={<TenantDetail />} />
                <Route path="/platform/users" element={<TenantUsers />} />
                <Route path="/platform/subscriptions" element={<Subscriptions />} />
                {/* Usage & Analytics merged into the platform dashboard
                    (FEAT-2026-006). Redirect keeps old bookmarks working. */}
                <Route path="/platform/analytics" element={<Navigate to="/platform" replace />} />
                <Route path="/platform/sites" element={<SitesOverview />} />
                <Route path="/platform/ai-control" element={<AIControl />} />
                <Route path="/platform/notifications" element={<GlobalNotifications />} />
                <Route path="/platform/audit" element={<AuditLogs />} />
                <Route path="/platform/settings" element={<PlatformSettings />} />
                <Route path="/platform/profile" element={<PlatformProfile />} />
              </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthProvider>
          </LocaleProvider>
        </ClerkWithRouter>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
